import { useEffect, useRef, useCallback } from "react";
import { useEmployeeInfo } from "../employee/useEmployeeInfo";
import { sseService } from "@/modules/services/sseService";
import { useNotificationStore } from "@/modules/store/notificationStore";
import {
  isMessageOverEvent,
  MessageOverEvent,
} from "@/modules/types/conversation/message";
import {
  isNotificationMessageDto,
  NotificationMessageDto,
} from "@/modules/types/notification/notification";
import useAllowNoti from "./useAllowNoti";
import { usePathname, useRouter } from "next/navigation";
import { EventSourcePolyfill } from "event-source-polyfill";
import { AppError } from "@/lib/utils/errors";
import { enqueueSnackbar } from "notistack";
import { useClientTranslations } from "@/lib/translate";
import { routes } from "@/lib/utils/routes";
import { useMessage } from "../conversation/useMessage";

const RECONNECT_INTERVAL = 5000; // 5 seconds

export const useSSE = () => {
  const { t } = useClientTranslations("notification");
  const { employeeInfo } = useEmployeeInfo();
  const { appendMessage } = useMessage();
  const { addNotification, addUnReadNotification } = useNotificationStore();
  const { isAllowed } = useAllowNoti();
  const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevEmployeeIdRef = useRef<string | undefined>(undefined);
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  const router = useRouter();

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // Helper to get token (async)
  const getToken = useCallback(async () => {
    if (sseService.getToken && typeof sseService.getToken === "function") {
      return sseService.getToken();
    }
    return undefined;
  }, []);

  useEffect(() => {
    const cleanup = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    if (!employeeInfo || !employeeInfo.id) {
      cleanup();
      prevEmployeeIdRef.current = undefined;
      return;
    }

    if (prevEmployeeIdRef.current !== employeeInfo.id) {
      cleanup();
      prevEmployeeIdRef.current = employeeInfo.id;
    }

    const notifyNotification = (noti: NotificationMessageDto) => {
      enqueueSnackbar(noti.title || "Bạn có thông báo mới", {
        autoHideDuration: 10000,
        // @ts-expect-error just add notification variant
        variant: "notification",
        notification: noti,
      });
      if (isAllowed) {
        const notification = new Notification(
          noti.title || t("newNotificationTitle"),
          {
            icon: noti.subject?.[0]?.image,
            body: noti.content,
          }
        );
        notification.onclick = () => {
          if (noti.actionUrl) {
            router.push(noti.actionUrl);
          } else {
            window.focus();
          }
        };
      }
    };

    const notifyNewMessage = (message: MessageOverEvent) => {
      if (!employeeInfo) {
        return;
      }
      if (
        message.senderName ===
        `${employeeInfo.firstName} ${employeeInfo.lastName}`
      ) {
        return;
      }
      if (
        pathnameRef.current ==
        `${routes.candidate.message}/${message.conversationId}`
      ) {
        return;
      }
      enqueueSnackbar(`${message.senderName} ${t("newMessageSubject")}`, {
        autoHideDuration: 10000,
        // @ts-expect-error just add notification variant
        variant: "notification",
        notification: message,
      });
      if (isAllowed) {
        const notification = new Notification(
          `${message.senderName} ${t("newMessageSubject")}`,
          {
            icon: message.image,
            body: message.content,
          }
        );
        notification.onclick = () => {
          router.push(`${routes.candidate.message}/${message.conversationId}`);
        };
      }
    };

    // Async connect to handle async token
    const connect = async () => {
      const endpoint = sseService.getSseEndpoint(employeeInfo.id);
      const token = await getToken();
      const eventSourceOptions = {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      };
      const es = new EventSourcePolyfill(endpoint, eventSourceOptions);
      eventSourceRef.current = es;

      es.onmessage = function (this: EventSource, event: any) {
        try {
          const parsedData = JSON.parse(event.data);
          const data = parsedData as NotificationMessageDto | MessageOverEvent;
          if (isNotificationMessageDto(data)) {
            addNotification(data as NotificationMessageDto);
            addUnReadNotification(data as NotificationMessageDto);
            notifyNotification(data as NotificationMessageDto);
          } else if (isMessageOverEvent(data)) {
            const message = data as MessageOverEvent;
            appendMessage(message.conversationId, message);
            notifyNewMessage(data as MessageOverEvent);
            // Push new message to messageStore for the conversation
          } else {
            const appErr = new AppError("Unknown data type");
            appErr.cause = data;
            appErr.message = "Unknown data type";
            appErr.code = 400;
            appErr.setAction("useSSE.onmessage[unknownDataType]");
            appErr.setApiAction("EventSource.onmessage");
            appErr.logError("Hook SSE Error On Message");
          }
        } catch (err) {
          const appErr = new AppError(err);
          appErr.setAction("useSSE.onmessage[parseError]");
          appErr.setApiAction("EventSource.onmessage");
          appErr.logError("Hook SSE Error On Message");
        }
      };

      es.onerror = () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_INTERVAL);
      };
    };

    connect();

    return () => {
      cleanup();
    };
  }, [employeeInfo, addNotification, isAllowed, router, getToken]);

  return {};
};
